import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { WS_TYPES } from "../wsProtocol";
import { MatchingBody, CategorizeBody, OrderingBody, HotspotBody } from "./InteractiveAnswers";
import VoiceRecorder from "./VoiceRecorder";

const INTERACTIVE_TYPES = ["matching", "categorize", "ordering", "hotspot"];

const format = (totalSeconds) => {
  const clamped = Math.max(0, Math.ceil(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// Shows the first open, unanswered pushed question. During a mini exam the
// questions surface one after another as each gets answered. Questions with
// retries left can be reopened for editing via a floating chip.
const AnswerModal = ({ activeQuestions, send, timeOffset, uploadBoardFile }) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");
  const [answerObj, setAnswerObj] = useState(null); // interactive types
  const [answerMode, setAnswerMode] = useState("text"); // writing: text | voice
  const [voiceFile, setVoiceFile] = useState(null);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [editingQid, setEditingQid] = useState(null);
  const [, forceTick] = useState(0);

  // instant teaching feedback takes priority over the next question
  const [dismissedFeedback, setDismissedFeedback] = useState([]);
  const feedbackQuestion = activeQuestions.find(
    (q) => q.answered && q.feedback && !dismissedFeedback.includes(q.qid)
  );

  const pending = activeQuestions.find((q) => !q.closed && !q.answered);
  const editable = activeQuestions.filter(
    (q) => !q.closed && q.answered && (q.ownAttempts || 1) < (q.maxAttempts || 1)
  );
  const editing = editingQid
    ? activeQuestions.find((q) => q.qid === editingQid && !q.closed)
    : null;
  const current = pending || editing;

  useEffect(() => {
    setAnswer("");
    setAnswerObj(null);
    setAnswerMode("text");
    setVoiceFile(null);
  }, [current?.qid]);

  // countdown ticks
  useEffect(() => {
    if (!current?.deadline) return;
    const interval = setInterval(() => forceTick((n) => n + 1), 500);
    return () => clearInterval(interval);
  }, [current?.deadline]);

  // when the edited question closes, drop the edit state
  useEffect(() => {
    if (editingQid && !editing) setEditingQid(null);
  }, [editingQid, editing]);

  // ---- result screen shown the moment an answer is submitted ----
  if (feedbackQuestion) {
    const fb = feedbackQuestion.feedback;
    const isPending = fb.kind === "pending";
    const isPartial = !isPending && !fb.correct && fb.score > 0;
    const tone = isPending
      ? { bg: "#e3f1fc", fg: "#0b4f7a", icon: "📨", title: t("wb_fb_pending") }
      : fb.correct
        ? { bg: "#e2f3e8", fg: "#12522a", icon: "🎉", title: t("wb_fb_correct") }
        : isPartial
          ? { bg: "#fdf0d7", fg: "#7a4c06", icon: "👍", title: t("wb_fb_partial") }
          : { bg: "#fbe5e5", fg: "#a83a3a", icon: "💡", title: t("wb_fb_wrong") };

    return (
      <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogContent sx={{ padding: "28px" }}>
          <Box sx={{ background: tone.bg, color: tone.fg, borderRadius: "16px", padding: "16px", textAlign: "center", marginBottom: 2 }}>
            <Typography fontSize="2.2rem" lineHeight={1}>{tone.icon}</Typography>
            <Typography variant="h6" fontWeight={800} mt={1}>{tone.title}</Typography>
            {!isPending && (
              <Typography fontSize="1rem" fontWeight={700} mt={0.5}>
                {t("wb_points")}: {fb.score} / {fb.maxScore}
              </Typography>
            )}
          </Box>

          <Typography fontWeight={700} mb={1.5}>{feedbackQuestion.question.title}</Typography>

          {/* multiple choice: every option, marked */}
          {fb.kind === "choose" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(fb.notes || (feedbackQuestion.question.options || []).map((o) => ({
                key: o.key, title: o.title, is_correct: o.key === fb.correctKey, explanation: "",
              }))).map((note) => {
                const isYours = note.key === fb.yourKey;
                return (
                  <Box
                    key={note.key}
                    sx={{
                      background: note.is_correct ? "#e8f5e9" : isYours ? "#ffebee" : "#f5f7fa",
                      borderInlineStart: `4px solid ${note.is_correct ? "#2e7d32" : isYours ? "#c62828" : "#cfd8dc"}`,
                      borderRadius: "10px",
                      padding: "10px 14px",
                    }}
                  >
                    <Typography fontWeight={800} fontSize="0.95rem">
                      {note.is_correct ? "✅" : "✖️"} {note.title}
                      {isYours && (
                        <Box component="span" sx={{
                          marginInlineStart: 1, fontSize: "0.7rem", background: "rgba(0,0,0,0.12)",
                          borderRadius: "999px", padding: "1px 8px", fontWeight: 700,
                        }}>
                          {t("wb_fb_your_answer")}
                        </Box>
                      )}
                    </Typography>
                    {note.explanation && (
                      <Typography fontSize="0.85rem" color="text.secondary" mt={0.4} lineHeight={1.7}>
                        {note.explanation}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* drag-and-drop types: show the model answer */}
          {fb.kind === "interactive" && (
            <>
              {fb.solutionText && (
                <Box sx={{ background: "#e8f5e9", borderInlineStart: "4px solid #2e7d32", borderRadius: "10px", padding: "12px 14px" }}>
                  <Typography fontSize="0.78rem" fontWeight={800} color="#2e7d32" mb={0.5}>
                    ✅ {t("wb_fb_model_answer")}
                  </Typography>
                  <Typography fontSize="0.95rem" lineHeight={1.8}>{fb.solutionText}</Typography>
                </Box>
              )}
              {fb.type === "hotspot" && feedbackQuestion.question.payload?.image && (
                <Box sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", marginTop: 1 }}>
                  <img src={feedbackQuestion.question.payload.image} alt="" style={{ width: "100%", display: "block" }} />
                  {fb.solution && (
                    <Box sx={{
                      position: "absolute",
                      left: `${fb.solution.x * 100}%`,
                      top: `${fb.solution.y * 100}%`,
                      width: `${fb.solution.r * 2 * 100}%`,
                      aspectRatio: "1",
                      transform: "translate(-50%, -50%)",
                      border: "4px solid #2e7d32",
                      borderRadius: "50%",
                      background: "rgba(46,125,50,0.18)",
                    }} />
                  )}
                </Box>
              )}
            </>
          )}

          {isPending && (
            <Typography color="text.secondary" fontSize="0.9rem">{t("wb_fb_pending_hint")}</Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ borderRadius: "100vh", marginTop: "20px", fontWeight: 700 }}
            onClick={() => setDismissedFeedback((prev) => [...prev, feedbackQuestion.qid])}
          >
            {t("wb_fb_got_it")}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (!current) {
    if (editable.length === 0) return null;
    return (
      <Box sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 29, display: "flex", gap: 1 }}>
        {editable.map((q) => (
          <Button
            key={q.qid}
            variant="contained"
            color="secondary"
            size="small"
            sx={{ borderRadius: "100vh", fontWeight: 700 }}
            onClick={() => setEditingQid(q.qid)}
          >
            ✏️ {t("wb_edit_answer")} ({q.ownAttempts}/{q.maxAttempts})
          </Button>
        ))}
      </Box>
    );
  }

  const question = current.question;
  const isChoose = question.type === "choose";
  const isInteractive = INTERACTIVE_TYPES.includes(question.type);
  const remaining = activeQuestions.filter((q) => !q.closed && !q.answered).length;

  const secondsLeft = current.deadline
    ? (current.deadline - (Date.now() + (timeOffset || 0))) / 1000
    : null;
  const timedOut = secondsLeft !== null && secondsLeft <= 0;

  const isVoice = question.type === "writing" && answerMode === "voice";
  const canSubmit = isInteractive ? answerObj !== null : isVoice ? voiceFile !== null : answer !== "";

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit || timedOut || isUploadingVoice) return;

    if (isVoice) {
      // upload the recording (board-token auth) then answer with its url
      setIsUploadingVoice(true);
      uploadBoardFile(voiceFile)
        .then((data) => {
          send({ t: WS_TYPES.Q_ANSWER, qid: current.qid, answer: JSON.stringify({ audio: data.url }) });
          if (editing) setEditingQid(null);
        })
        .catch(() => { })
        .finally(() => setIsUploadingVoice(false));
      return;
    }

    const payload = isInteractive ? JSON.stringify(answerObj) : String(answer);
    send({ t: WS_TYPES.Q_ANSWER, qid: current.qid, answer: payload });
    if (editing) setEditingQid(null);
  };

  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ padding: "30px" }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", marginBottom: "10px" }}>
          {current.exit && (
            <Chip size="small" color="secondary" label={`🎫 ${t("wb_exit_ticket")}`} sx={{ fontWeight: 700 }} />
          )}
          {remaining > 1 && (
            <Chip size="small" label={t("wb_questions_remaining", { count: remaining })} />
          )}
          {secondsLeft !== null && (
            <Chip
              size="small"
              color={secondsLeft < 15 ? "error" : "default"}
              label={`⏳ ${format(secondsLeft)}`}
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
            />
          )}
        </Box>

        <Typography variant="h6" mb={1}>{question.title}</Typography>
        <Typography color="text.secondary" fontSize="0.85rem" mb={2}>
          {question.score} {t("wb_points")}
        </Typography>

        <form onSubmit={submit}>
          {isChoose && (
            <RadioGroup value={answer} onChange={(event) => setAnswer(event.target.value)}>
              {(question.options || []).map((option) => (
                <FormControlLabel
                  key={option.key}
                  value={option.key}
                  control={<Radio />}
                  label={option.title}
                  disabled={timedOut}
                />
              ))}
            </RadioGroup>
          )}
          {question.type === "writing" && (
            <Box>
              <Box sx={{ display: "flex", gap: 1, marginBottom: 1.5 }}>
                <Button
                  size="small"
                  variant={answerMode === "text" ? "contained" : "outlined"}
                  onClick={() => setAnswerMode("text")}
                  sx={{ borderRadius: "100vh", fontWeight: 700 }}
                >
                  ✍️ {t("wb_answer_text")}
                </Button>
                <Button
                  size="small"
                  variant={answerMode === "voice" ? "contained" : "outlined"}
                  onClick={() => setAnswerMode("voice")}
                  sx={{ borderRadius: "100vh", fontWeight: 700 }}
                >
                  🎤 {t("wb_answer_voice")}
                </Button>
              </Box>
              {answerMode === "text" ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  placeholder={t("wb_write_answer")}
                  inputProps={{ maxLength: 2000 }}
                  disabled={timedOut}
                />
              ) : (
                <VoiceRecorder onRecorded={setVoiceFile} />
              )}
            </Box>
          )}
          {question.type === "matching" && <MatchingBody payload={question.payload} onChange={setAnswerObj} />}
          {question.type === "categorize" && <CategorizeBody payload={question.payload} onChange={setAnswerObj} />}
          {question.type === "ordering" && <OrderingBody payload={question.payload} onChange={setAnswerObj} />}
          {question.type === "hotspot" && <HotspotBody payload={question.payload} onChange={setAnswerObj} />}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!canSubmit || timedOut || isUploadingVoice}
            sx={{ borderRadius: "100vh", marginTop: "20px" }}
          >
            {timedOut ? t("wb_time_up") : isUploadingVoice ? "..." : t("wb_submit_answer")}
          </Button>
          {editing && (
            <Button fullWidth sx={{ marginTop: 1, borderRadius: "100vh" }} onClick={() => setEditingQid(null)}>
              {t("wb_cancel_edit")}
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AnswerModal;
